import React, { useEffect } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Code2,
  Gauge,
  GraduationCap,
  Landmark,
  LockKeyhole,
  ShieldCheck,
  ShoppingBag,
  Store,
  UsersRound,
  Zap,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import OfferGrid from '../components/OfferGrid';
import PageMeta from '../components/PageMeta';
import { useLanguage } from '../i18n/LanguageProvider';

const strengths = [
  { icon: Zap, title: 'Mise en œuvre rapide', text: 'Des parcours structurés pour accélérer le cadrage, l’intégration et le démarrage.' },
  { icon: LockKeyhole, title: 'Sécurité intégrée', text: 'La sécurité, la traçabilité et le contrôle des accès sont pensés dès la conception.' },
  { icon: Gauge, title: 'Pilotage en temps réel', text: 'Suivez les statuts, les volumes et les événements importants depuis vos outils.' },
  { icon: BarChart3, title: 'Évolutif par nature', text: 'Une architecture adaptée aussi bien aux petites équipes qu’aux opérations à grand volume.' },
];

const useCases = [
  { icon: ShoppingBag, title: 'Commerce en ligne', text: 'Encaissement Mobile Money, suivi de commande et remboursement.' },
  { icon: GraduationCap, title: 'Éducation', text: 'Paiement des frais, identification des élèves et rapprochement.' },
  { icon: Landmark, title: 'Institutions', text: 'Collectes, décaissements et circuits de validation structurés.' },
  { icon: UsersRound, title: 'Entreprises', text: 'Salaires, commissions, fournisseurs et paiements groupés.' },
  { icon: Store, title: 'Réseaux marchands', text: 'Caisses, QR de collecte et consolidation multi-points.' },
  { icon: Code2, title: 'Plateformes numériques', text: 'API de paiement intégrée à votre application ou service.' },
];

const Solutions = () => {
  const { t } = useLanguage();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const targetId = decodeURIComponent(hash.slice(1));
    const animationFrame = window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [hash]);

  return (
    <div className="ws-page ws-solutions" data-no-i18n="true">
      <PageMeta
        title="Solutions de paiement | WonyaSoft"
        description="Découvrez l’API de paiement WonyaPay, BulkPay et l’application mobile WonyaPay."
      />

      <section className="ws-page-hero ws-page-hero--solutions">
        <div className="ws-shell ws-page-hero__content">
          <span className="ws-eyebrow"><BadgeCheck size={18} aria-hidden="true" /> {t('Nos solutions numériques de paiement')}</span>
          <h1>{t('Des parcours de paiement conçus pour vos usages réels')}</h1>
          <p>{t('API, paiements en masse ou encaissement mobile : WonyaSoft réunit les briques nécessaires pour déployer des services de paiement fiables en RDC.')}</p>
          <div className="ws-button-row">
            <a className="ws-button ws-button--primary" href="#solutions-offers">{t('Explorer les solutions')} <ArrowRight size={18} aria-hidden="true" /></a>
            <Link className="ws-button ws-button--secondary" to="/contact">{t('Parler à notre équipe')}</Link>
          </div>
        </div>
      </section>

      <section id="solutions-offers" className="ws-section" aria-labelledby="ws-solutions-offers-title">
        <div className="ws-shell">
          <div className="ws-section-heading">
            <span className="ws-kicker">{t('Un écosystème cohérent')}</span>
            <h2 id="ws-solutions-offers-title">{t('Trois façons d’avancer avec WonyaSoft')}</h2>
            <p>{t('Choisissez une solution prête à intégrer ou construisons ensemble un parcours adapté à votre organisation.')}</p>
          </div>
          <OfferGrid />
        </div>
      </section>

      <section className="ws-section ws-section--soft" aria-labelledby="ws-solutions-strengths-title">
        <div className="ws-shell">
          <div className="ws-section-heading">
            <span className="ws-kicker">{t('Pourquoi WonyaSoft')}</span>
            <h2 id="ws-solutions-strengths-title">{t('Une base solide pour vos opérations')}</h2>
            <p>{t('Nos solutions sont pensées pour les contraintes de terrain, la croissance et la continuité de service.')}</p>
          </div>
          <div className="ws-solution-strength-grid">
            {strengths.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <Icon size={24} aria-hidden="true" />
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section ws-section--dark" aria-labelledby="ws-solutions-use-cases-title">
        <div className="ws-shell ws-solutions-use-cases">
          <div className="ws-section-heading">
            <span className="ws-kicker">{t('Cas d’usage')}</span>
            <h2 id="ws-solutions-use-cases-title">{t('Des outils adaptés à plusieurs secteurs')}</h2>
            <p>{t('Le même socle technologique s’adapte à votre organisation, vos équipes et vos flux.')}</p>
          </div>
          <div className="ws-use-case-grid">
            {useCases.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <Icon size={22} aria-hidden="true" />
                <div><h3>{t(title)}</h3><p>{t(text)}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section">
        <div className="ws-shell ws-final-cta">
          <div>
            <span className="ws-kicker"><ShieldCheck size={16} aria-hidden="true" /> WonyaSoft</span>
            <h2>{t('Construisons votre prochain parcours de paiement')}</h2>
            <p>{t('Notre équipe vous accompagne du cadrage jusqu’à l’intégration et la mise en production.')}</p>
          </div>
          <Link className="ws-button ws-button--primary" to="/contact">{t('Nous contacter')} <ArrowRight size={18} aria-hidden="true" /></Link>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
